/**
 * Roles and the permission catalogue. Owner-only surface — every route is gated on `role.view`,
 * `role.create`, `role.edit` or `role.assign_permissions`, checked server-side.
 *
 * `listPermissions` is the catalogue the server knows about, not any one role's grants — it
 * changes only when the backend adds a permission, so one static tag is enough. Assigning
 * permissions to a role is a full replace (the server takes the complete desired set, not a
 * delta), which is why `assignRolePermissions` sends the whole array every time rather than an
 * add/remove pair.
 */

import { api } from '../api.js';
import { permissionCatalogueTag, roleListTag, roleTag } from '../tags.js';
import type { PermissionInfo, Role, RoleId } from '../../types/api.js';

export type CreateRoleBody = {
  readonly name: string;
  readonly description?: string | undefined;
  readonly permissions?: readonly string[] | undefined;
};

export type UpdateRoleBody = {
  readonly name: string;
  readonly description?: string | undefined;
};

export const rolesApi = api.injectEndpoints({
  endpoints: (build) => ({
    listRoles: build.query<Role[], { includeInactive?: boolean | undefined } | void>({
      query: (params) => ({ url: '/roles', params: params ?? {} }),
      providesTags: (result) => [
        roleListTag(),
        ...(result ?? []).map((role) => roleTag(role.id)),
      ],
    }),

    listPermissions: build.query<PermissionInfo[], void>({
      query: () => '/roles/permissions',
      providesTags: [permissionCatalogueTag()],
    }),

    createRole: build.mutation<RoleId, CreateRoleBody>({
      query: (body) => ({ url: '/roles', method: 'POST', body }),
      invalidatesTags: [roleListTag()],
    }),

    updateRole: build.mutation<void, { id: RoleId; body: UpdateRoleBody }>({
      query: ({ id, body }) => ({ url: `/roles/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [roleTag(id), roleListTag()],
    }),

    /** Full replace — the Owner role rejects this, its grants are re-synced on every boot instead. */
    assignRolePermissions: build.mutation<void, { id: RoleId; permissions: readonly string[] }>({
      query: ({ id, permissions }) => ({
        url: `/roles/${id}/permissions`,
        method: 'PUT',
        body: { permissions },
      }),
      invalidatesTags: (_result, _error, { id }) => [roleTag(id)],
    }),
  }),
});

export const {
  useListRolesQuery,
  useListPermissionsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useAssignRolePermissionsMutation,
} = rolesApi;
